import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/20 hover:text-destructive text-left",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-gray-100 border border-gray-200 text-gray-500 ",
        ghost: "font-bold p-0 m-0 text-gray-900",
        link: "text-primary underline-offset-4 hover:underline",

        hollowGreen:
          "px-4 py-2 bg-white text-[#14B8A6] border-[#14B8A6] border-[1px] rounded-md hover:bg-dropdown-hover ",

        // DropDown styles
        dropPlain:
          "bg-dropdown text-dropdown-foreground hover:bg-dropdown-hover hover:text-green-600 w-full text-left",
        dropRed:
          "bg-dropdown text-destructive hover:bg-destructive-hover w-full text-left",
        dropGreen:
          "bg-dropdown text-primary-foreground hover:bg-dropdown-hover text-green-600 w-full text-left",
        dropMenu:
          "flex items-center justify-center p-2 text-gray-600 hover:text-gray-900",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
